<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { fade, fly, scale } from 'svelte/transition';
	import { onMount } from 'svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	let visible = $state(true); // SSR: start visible so hero + <h1> are in server HTML (Ahrefs: "H1 tag missing"). onMount re-set is a no-op; entrance animations still play on client-side navigation.
	let activeProject = $state(0);

	onMount(() => {
		visible = true;
		// Simulate active project switching
		const interval = setInterval(() => {
			activeProject = (activeProject + 1) % 3;
		}, 2500);
		return () => clearInterval(interval);
	});

	const chaosScenarios = [
		{
			title: $_('features.projectManagement.chaosScenarios.scenario1.title'),
			scene: $_('features.projectManagement.chaosScenarios.scenario1.scene'),
			solution: $_('features.projectManagement.chaosScenarios.scenario1.solution')
		},
		{
			title: $_('features.projectManagement.chaosScenarios.scenario2.title'),
			scene: $_('features.projectManagement.chaosScenarios.scenario2.scene'),
			solution: $_('features.projectManagement.chaosScenarios.scenario2.solution')
		},
		{
			title: $_('features.projectManagement.chaosScenarios.scenario3.title'),
			scene: $_('features.projectManagement.chaosScenarios.scenario3.scene'),
			solution: $_('features.projectManagement.chaosScenarios.scenario3.solution')
		}
	];

	const projectDemo = [
		{
			name: $_('features.projectManagement.projectDemo.project1.name'),
			status: $_('features.projectManagement.projectDemo.project1.status'),
			progress: 67,
			budget: $_('features.projectManagement.projectDemo.project1.budget'),
			spent: $_('features.projectManagement.projectDemo.project1.spent'),
			deadline: $_('features.projectManagement.projectDemo.project1.deadline'),
			color: '#10b981'
		},
		{
			name: $_('features.projectManagement.projectDemo.project2.name'),
			status: $_('features.projectManagement.projectDemo.project2.status'),
			progress: 23,
			budget: $_('features.projectManagement.projectDemo.project2.budget'),
			spent: $_('features.projectManagement.projectDemo.project2.spent'),
			deadline: $_('features.projectManagement.projectDemo.project2.deadline'),
			color: '#3b82f6'
		},
		{
			name: $_('features.projectManagement.projectDemo.project3.name'),
			status: $_('features.projectManagement.projectDemo.project3.status'),
			progress: 89,
			budget: $_('features.projectManagement.projectDemo.project3.budget'),
			spent: $_('features.projectManagement.projectDemo.project3.spent'),
			deadline: $_('features.projectManagement.projectDemo.project3.deadline'),
			color: '#f59e0b'
		}
	];

	const features = [
		{
			icon: 'bar-chart',
			title: $_('features.projectManagement.features.feature1.title'),
			description: $_('features.projectManagement.features.feature1.description'),
			detail: $_('features.projectManagement.features.feature1.detail')
		},
		{
			icon: 'users',
			title: $_('features.projectManagement.features.feature2.title'),
			description: $_('features.projectManagement.features.feature2.description'),
			detail: $_('features.projectManagement.features.feature2.detail')
		},
		{
			icon: 'trending-up',
			title: $_('features.projectManagement.features.feature3.title'),
			description: $_('features.projectManagement.features.feature3.description'),
			detail: $_('features.projectManagement.features.feature3.detail')
		}
	];
</script>

<svelte:head>
	<title>{$_('features.projectManagement.meta.title')}</title>
	<meta name="description" content={$_('features.projectManagement.meta.description')} />
	<meta property="og:title" content="{$_('features.projectManagement.meta.title')}" />
	<meta property="og:description" content={$_('features.projectManagement.meta.description')} />
</svelte:head>

<Header />

<div class="feature-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			{#if visible}
				<div class="hero__content" in:fade={{ duration: 600, delay: 100 }}>
					<div class="hero__chaos" in:fly={{ y: 30, duration: 600, delay: 200 }}>
						<h1 class="hero__title">
							{@html $_('features.projectManagement.hero.title')}
						</h1>
					</div>
					<p class="hero__subtitle" in:fly={{ y: 30, duration: 600, delay: 400 }}>
						{$_('features.projectManagement.hero.subtitle')}
					</p>
					<div class="hero__actions" in:fly={{ y: 30, duration: 600, delay: 500 }}>
						<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
							{$_('features.projectManagement.hero.primaryCta')}
						</Button>
						<Button variant="ghost" size="lg" href="#chaos">
							{$_('features.projectManagement.hero.secondaryCta')}
						</Button>
					</div>
				</div>
			{/if}
		</div>
	</section>

	<!-- Chaos Scenarios -->
	<section class="chaos-scenarios" id="chaos">
		<div class="container">
			<div class="chaos-scenarios__header">
				<h2 class="chaos-scenarios__title">
					{$_('features.projectManagement.chaosScenarios.title')}
				</h2>
			</div>
			<div class="scenarios-grid">
				{#each chaosScenarios as scenario, index}
					<div class="scenario-card" style="animation-delay: {index * 200}ms">
						<div class="scenario-card__header">
							<Icon name="alert-circle" size={20} style="color: #ef4444;" />
							<h3 class="scenario-card__title">{scenario.title}</h3>
						</div>
						<p class="scenario-card__scene">{scenario.scene}</p>
						<div class="scenario-card__divider"></div>
						<div class="scenario-card__solution">
							<Icon name="check-circle" size={16} style="color: rgb(255, 94, 126);" />
							<p>{scenario.solution}</p>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Dashboard Demo -->
	<section class="dashboard-demo">
		<div class="container">
			<div class="dashboard-demo__header">
				<h2 class="dashboard-demo__title">
					{$_('features.projectManagement.dashboardDemo.title')}
				</h2>
				<p class="dashboard-demo__subtitle">
					{$_('features.projectManagement.dashboardDemo.subtitle')}
				</p>
			</div>
			<div class="project-dashboard">
				<div class="dashboard-header">
					<h3 class="dashboard-title">
						{$_('features.projectManagement.dashboardDemo.activeProjects')}
					</h3>
					<div class="dashboard-stats">
						<div class="stat">
							<div class="stat__number">3</div>
							<div class="stat__label">{$_('features.projectManagement.dashboardDemo.active')}</div>
						</div>
						<div class="stat">
							<div class="stat__number">1</div>
							<div class="stat__label">{$_('features.projectManagement.dashboardDemo.urgent')}</div>
						</div>
						<div class="stat">
							<div class="stat__number">89%</div>
							<div class="stat__label">
								{$_('features.projectManagement.dashboardDemo.onTrack')}
							</div>
						</div>
					</div>
				</div>
				<div class="projects-list">
					{#each projectDemo as project, index}
						<div class="project-card" class:active={index === activeProject}>
							<div class="project-card__main">
								<div class="project-card__header">
									<h4 class="project-card__name">{project.name}</h4>
									<span class="project-card__status project-card__status--{project.status}">
										{project.status}
									</span>
								</div>
								<div class="project-card__progress">
									<div class="progress-bar">
										<div
											class="progress-bar__fill"
											style="width: {project.progress}%; background: {project.color}"
										></div>
									</div>
									<span class="progress-text">{project.progress}%</span>
								</div>
							</div>
							<div class="project-card__details">
								<div class="project-detail">
									<Icon name="dollar-sign" size={16} />
									<span>{project.spent} / {project.budget}</span>
								</div>
								<div class="project-detail">
									<Icon name="clock" size={16} />
									<span>{project.deadline}</span>
								</div>
							</div>
						</div>
					{/each}
				</div>
			</div>
		</div>
	</section>

	<!-- Features Section -->
	<section class="features">
		<div class="container">
			<div class="features__header">
				<h2 class="features__title">{$_('features.projectManagement.features.title')}</h2>
			</div>
			<div class="features__list">
				{#each features as feature}
					<div class="feature-item">
						<div class="feature-item__icon">
							<Icon name={feature.icon} size={32} style="color: rgb(255, 94, 126);" />
						</div>
						<div class="feature-item__content">
							<h3 class="feature-item__title">{feature.title}</h3>
							<p class="feature-item__description">{feature.description}</p>
							<p class="feature-item__detail">{feature.detail}</p>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<section class="cta">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('features.projectManagement.cta.title')}</h2>
				<p class="cta__subtitle">
					{$_('features.projectManagement.cta.subtitle')}
				</p>
				<div class="cta__actions">
					<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
						{$_('features.projectManagement.cta.primaryButton')}
					</Button>
					<Button variant="ghost" size="lg" href="/pricing">
						{$_('features.projectManagement.cta.secondaryButton')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="project-management" />

<Footer />

<style>
	.feature-page {
		min-height: 100vh;
	}

	/* Hero Section - Chaotic layout */
	.hero {
		padding: var(--space-20) 0;
		background: linear-gradient(
			45deg,
			var(--bg-secondary) 0%,
			transparent 30%,
			rgba(255, 94, 126, 0.05) 100%
		);
		position: relative;
		overflow: hidden;
	}

	.hero__content {
		text-align: center;
		max-width: 900px;
		margin: 0 auto;
		position: relative;
		z-index: 2;
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__strikethrough {
		position: relative;
		color: var(--text-secondary);
	}

	.hero__strikethrough::after {
		content: '';
		position: absolute;
		left: 0;
		top: 50%;
		width: 100%;
		height: 3px;
		background: #ef4444;
		transform: rotate(-8deg);
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		max-width: 700px;
		margin: 0 auto var(--space-8);
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	.hero__visual {
		position: absolute;
		right: 5%;
		top: 50%;
		transform: translateY(-50%);
		z-index: 1;
	}

	/* Chaos Animation */
	.projects-chaos {
		position: relative;
		width: 300px;
		height: 300px;
	}

	.chaos-items {
		position: relative;
		width: 100%;
		height: 100%;
	}

	.chaos-item {
		position: absolute;
		padding: var(--space-2) var(--space-3);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-md);
		font-size: var(--font-size-sm);
		color: var(--text-primary);
		white-space: nowrap;
		animation: float 3s ease-in-out infinite;
		box-shadow: var(--shadow-md);
	}

	.chaos-item.deadline {
		top: 10%;
		left: 50%;
		transform: translateX(-50%) rotate(-5deg);
		border-color: #ef4444;
		color: #ef4444;
		animation-delay: 0s;
	}

	.chaos-item.budget {
		top: 30%;
		left: 80%;
		transform: translateX(-50%) rotate(8deg);
		border-color: #f59e0b;
		color: #f59e0b;
		animation-delay: 0.5s;
	}

	.chaos-item.email {
		top: 60%;
		left: 15%;
		transform: rotate(-12deg);
		border-color: #3b82f6;
		color: #3b82f6;
		animation-delay: 1s;
	}

	.chaos-item.todo {
		top: 80%;
		left: 70%;
		transform: rotate(6deg);
		border-color: #8b5cf6;
		color: #8b5cf6;
		animation-delay: 1.5s;
	}

	.chaos-item.meeting {
		top: 45%;
		left: 5%;
		transform: rotate(-3deg);
		border-color: #10b981;
		color: #10b981;
		animation-delay: 2s;
	}

	.chaos-center {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		text-align: center;
		background: var(--bg-primary);
		border: 2px solid #ef4444;
		border-radius: var(--radius-full);
		width: 80px;
		height: 80px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		box-shadow: var(--shadow-lg);
	}

	.chaos-center p {
		margin: 0;
		font-size: var(--font-size-xs);
		color: #ef4444;
		font-weight: var(--font-weight-semibold);
		margin-top: var(--space-1);
	}

	@keyframes float {
		0%,
		100% {
			transform: translateY(0) rotate(var(--rotation, 0deg));
		}
		50% {
			transform: translateY(-10px) rotate(var(--rotation, 0deg));
		}
	}

	/* Chaos Scenarios */
	.chaos-scenarios {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.chaos-scenarios__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.chaos-scenarios__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		letter-spacing: -0.02em;
	}

	.scenarios-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
		gap: var(--space-8);
		max-width: 1200px;
		margin: 0 auto;
	}

	.scenario-card {
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		animation: slideUp 0.6s ease-out both;
	}

	@keyframes slideUp {
		from {
			opacity: 0;
			transform: translateY(30px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.scenario-card__header {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		margin-bottom: var(--space-4);
	}

	.scenario-card__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0;
	}

	.scenario-card__scene {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-4);
		font-style: italic;
	}

	.scenario-card__divider {
		height: 1px;
		background: var(--border-light);
		margin: var(--space-4) 0;
	}

	.scenario-card__solution {
		display: flex;
		align-items: flex-start;
		gap: var(--space-3);
	}

	.scenario-card__solution p {
		font-size: var(--font-size-sm);
		color: var(--text-primary);
		line-height: 1.5;
		margin: 0;
	}

	/* Dashboard Demo */
	.dashboard-demo {
		padding: var(--space-20) 0;
	}

	.dashboard-demo__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.dashboard-demo__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.dashboard-demo__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
	}

	.project-dashboard {
		max-width: 800px;
		margin: 0 auto;
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-xl);
		padding: var(--space-8);
		box-shadow: var(--shadow-xl);
	}

	.dashboard-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: var(--space-8);
		padding-bottom: var(--space-4);
		border-bottom: 1px solid var(--border-light);
	}

	.dashboard-title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0;
	}

	.dashboard-stats {
		display: flex;
		gap: var(--space-6);
	}

	.stat {
		text-align: center;
	}

	.stat__number {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-bold);
		color: rgb(255, 94, 126);
	}

	.stat__label {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		margin-top: var(--space-1);
	}

	.projects-list {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.project-card {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: var(--space-4) var(--space-6);
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		transition: all 0.3s ease;
	}

	.project-card.active {
		background: rgba(255, 94, 126, 0.05);
		border-color: rgb(255, 94, 126);
		transform: scale(1.02);
	}

	.project-card__main {
		flex: 1;
	}

	.project-card__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: var(--space-3);
	}

	.project-card__name {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0;
	}

	.project-card__status {
		font-size: var(--font-size-xs);
		padding: var(--space-1) var(--space-2);
		border-radius: var(--radius-sm);
		text-transform: uppercase;
		font-weight: var(--font-weight-medium);
		letter-spacing: 0.05em;
	}

	.project-card__status--active {
		background: #dcfce7;
		color: #16a34a;
	}

	.project-card__status--planning {
		background: #dbeafe;
		color: #2563eb;
	}

	.project-card__status--urgent {
		background: #fef3c7;
		color: #d97706;
	}

	.project-card__progress {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		margin-bottom: var(--space-3);
	}

	.progress-bar {
		flex: 1;
		height: 8px;
		background: var(--border-light);
		border-radius: var(--radius-full);
		overflow: hidden;
	}

	.progress-bar__fill {
		height: 100%;
		transition: width 0.3s ease;
		border-radius: var(--radius-full);
	}

	.progress-text {
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		color: var(--text-secondary);
	}

	.project-card__details {
		display: flex;
		gap: var(--space-4);
	}

	.project-detail {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
	}

	/* Features Section */
	.features {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.features__header {
		text-align: center;
		margin-bottom: var(--space-16);
	}

	.features__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		letter-spacing: -0.02em;
	}

	.features__list {
		max-width: 1000px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		gap: var(--space-12);
	}

	.feature-item {
		display: grid;
		grid-template-columns: auto 1fr;
		gap: var(--space-6);
		align-items: flex-start;
	}

	.feature-item__icon {
		width: 80px;
		height: 80px;
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		display: flex;
		align-items: center;
		justify-content: center;
		box-shadow: var(--shadow-md);
	}

	.feature-item__title {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0 0 var(--space-3) 0;
		letter-spacing: -0.02em;
	}

	.feature-item__description {
		font-size: var(--font-size-lg);
		color: var(--text-primary);
		line-height: 1.6;
		margin: 0 0 var(--space-2) 0;
	}

	.feature-item__detail {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.5;
		margin: 0;
		font-style: italic;
	}

	/* CTA Section */
	.cta {
		padding: var(--space-20) 0;
		text-align: center;
		background: linear-gradient(180deg, transparent 0%, var(--bg-secondary) 100%);
	}

	.cta__content {
		max-width: 700px;
		margin: 0 auto;
	}

	.cta__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.cta__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.cta__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	/* Responsive */
	@media (max-width: 768px) {
		.hero__visual {
			display: none;
		}

		.hero__title {
			font-size: 2rem;
		}

		.scenarios-grid {
			grid-template-columns: 1fr;
		}

		.dashboard-stats {
			gap: var(--space-4);
		}

		.project-card {
			flex-direction: column;
			align-items: stretch;
			gap: var(--space-4);
		}

		.feature-item {
			grid-template-columns: 1fr;
			text-align: center;
		}
	}
</style>
